import { useState, useEffect } from "react";
import { Radar } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ResponsiveContainer,
} from "recharts";
import {
  CHART_HEIGHT, AXIS_TICK, AXIS_STROKE, GRID_STROKE, TOOLTIP_STYLE,
} from "../lib/chartTokens";

const FIELD_COLORS = {
  "Healthcare": "#10b981",
  "Education": "#3b82f6",
  "Finance": "#f59e0b",
  "Productivity": "#8b5cf6",
  "Creative": "#ec4899",
  "E-commerce": "#06b6d4",
  "Social": "#f97316",
  "Developer Tools": "#6366f1",
  "Marketing": "#14b8a6",
  "Environment": "#22c55e",
  "Other": "#6b7280",
};

export const FieldDistributionChart = () => {
  const [fieldData, setFieldData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFieldData = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/field-distribution`);
        const data = await response.json();
        setFieldData(data);
      } catch {
        void 0;
      } finally {
        setLoading(false);
      }
    };
    fetchFieldData();
  }, []);

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="text-base font-semibold mb-4 flex items-center gap-2">
          <Radar className="w-4 h-4 text-primary" />
          AI Impact by Field
        </h3>
        <div className={`${CHART_HEIGHT} flex items-center justify-center text-muted-foreground`}>
          Loading field distribution...
        </div>
      </div>
    );
  }

  if (!fieldData) return null;

  const chartData = fieldData.field_distribution.map(item => ({
    name: item.field,
    value: item.count,
    percentage: item.percentage,
    fill: FIELD_COLORS[item.field] || "#6b7280",
  }));

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <h3 className="text-base font-semibold mb-4 flex items-center gap-2">
        <Radar className="w-4 h-4 text-primary" />
        AI Impact by Field
        <span className="text-xs font-normal text-muted-foreground ml-1">
          ({fieldData.total_apps} apps analyzed)
        </span>
      </h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
              <XAxis type="number" stroke={AXIS_STROKE} tick={AXIS_TICK} />
              <YAxis
                dataKey="name"
                type="category"
                stroke={AXIS_STROKE}
                width={110}
                tick={{ ...AXIS_TICK, fontSize: 11 }}
                interval={0}
              />
              <Tooltip
                {...TOOLTIP_STYLE}
                formatter={(value, _name, props) => [`${value} apps (${props.payload.percentage}%)`, "Count"]}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {chartData.map((entry) => (
                  <Cell key={`cell-${entry.name}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {fieldData.field_distribution.slice(0, 9).map((field) => (
            <div
              key={field.field}
              className="bg-muted/30 border border-border rounded-lg p-3 hover:bg-muted/50 transition-colors"
            >
              <div
                className="text-xl font-bold"
                style={{ color: FIELD_COLORS[field.field] || "#6b7280" }}
              >
                {field.percentage}%
              </div>
              <div className="text-xs font-medium">{field.field}</div>
              <div className="text-xs text-muted-foreground">{field.count} apps</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FieldDistributionChart;
